import { Alert, AlertTitle, Button, Container, Stack, Typography } from "@mui/material";
import { Component } from "react";
import { hadRecentAuthorizationError } from "utils/apiErrors";
import { captureRenderError } from "utils/errorTracking";

/**
 * Catches render crashes below it and shows a message instead of a blank page.
 *
 * A request rejected by a role gate leaves the page without the data it renders from, which almost
 * always ends in a `TypeError` during render — React then unmounts the whole tree and the user sees
 * a white screen. When the crash follows a 403 we say so explicitly; otherwise we show a generic
 * failure with a way out. The crash goes to Sentry (utils/errorTracking.js): an error a boundary
 * catches never reaches the window's error handler.
 *
 * @param {React.ReactNode} children - Subtree to guard.
 * @param {any} resetKey - Changing this clears the fallback (pass the current route).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, deniedPermission: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true, deniedPermission: hadRecentAuthorizationError() };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary] Render failed:", error, errorInfo?.componentStack);
    captureRenderError(error, errorInfo?.componentStack, {
      permissionDenied: this.state.deniedPermission,
    });
  }

  componentDidUpdate(prevProps) {
    // Navigating away from the broken view puts the app back in a usable state.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, deniedPermission: false });
    }
  }

  render() {
    const { hasError, deniedPermission } = this.state;

    if (!hasError) return this.props.children;

    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity={deniedPermission ? "warning" : "error"} variant="outlined">
          <AlertTitle>
            {deniedPermission ? "Keine Berechtigung" : "Es ist ein Fehler aufgetreten"}
          </AlertTitle>
          <Typography variant="body2">
            {deniedPermission
              ? "Sie haben keine Berechtigung für diesen Bereich, deshalb konnte die Seite nicht geladen werden. Bitte wenden Sie sich an einen Administrator, wenn Sie Zugriff benötigen."
              : "Die Seite konnte nicht angezeigt werden. Bitte laden Sie die Seite neu oder kehren Sie zur Übersicht zurück."}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="contained" onClick={() => window.location.reload()}>
              Seite neu laden
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                window.location.href = "/dashboard";
              }}
            >
              Zur Übersicht
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }
}
